import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plug } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUpdateShellyCredentials } from '@/api/hooks/usePlatformTenantDetail';
import type { AppError } from '@/api/client';
import { CredentialsStatusBadge } from './CredentialsStatusBadge';

const shellySchema = z.object({
  authKey: z.string().min(1, 'required'),
  serverUrl: z.union([z.string().url(), z.literal('')]).optional(),
});

type ShellyForm = z.infer<typeof shellySchema>;

type Props = {
  tenantId: string;
  isConfigured: boolean;
};

export function ShellyCredentialsCard({ tenantId, isConfigured }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const mutation = useUpdateShellyCredentials(tenantId);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ShellyForm>({
    resolver: zodResolver(shellySchema) as never,
    defaultValues: {
      authKey: '',
      serverUrl: '',
    },
  });

  const close = () => {
    setOpen(false);
    setServerError(null);
    reset();
  };

  const onSubmit = async (data: ShellyForm) => {
    setServerError(null);
    try {
      const result = await mutation.mutateAsync({
        authKey: data.authKey,
        serverUrl: data.serverUrl || undefined,
      });
      if (result.credentialsSet) {
        toast.success(t('platform_admin.tenant_detail.credentials.shelly.save_success'));
        close();
      } else {
        setServerError(result.message);
      }
    } catch (err) {
      const appErr = err as AppError;
      setServerError(appErr.message ?? t('common.error'));
    }
  };

  const fieldError = (key: keyof ShellyForm) => {
    const e = errors[key];
    if (!e?.message) return null;
    return t(`platform_admin.tenant_detail.credentials.errors.${e.message}`);
  };

  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-neutral-100 p-2">
            <Plug className="h-5 w-5 text-neutral-700" aria-hidden="true" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-neutral-900">
              {t('platform_admin.tenant_detail.credentials.shelly.title')}
            </h3>
            <p className="mt-1 text-sm text-neutral-500">
              {t('platform_admin.tenant_detail.credentials.shelly.description')}
            </p>
          </div>
        </div>
        <CredentialsStatusBadge isConfigured={isConfigured} />
      </div>

      <div className="mt-4">
        <Button
          type="button"
          variant={isConfigured ? 'outline' : 'default'}
          onClick={() => setOpen(true)}
        >
          {isConfigured
            ? t('platform_admin.tenant_detail.credentials.update_button')
            : t('platform_admin.tenant_detail.credentials.shelly.configure_button')}
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('platform_admin.tenant_detail.credentials.shelly.modal_title')}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="shelly-authKey">
                {t('platform_admin.tenant_detail.credentials.shelly.fields.auth_key')}
              </Label>
              <Input
                id="shelly-authKey"
                type="password"
                autoComplete="new-password"
                {...register('authKey')}
              />
              <p className="text-[11px] text-neutral-400">
                {t('platform_admin.tenant_detail.credentials.shelly.fields.auth_key_hint')}
              </p>
              {fieldError('authKey') && (
                <p className="text-xs text-red-600">{fieldError('authKey')}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="shelly-serverUrl">
                {t('platform_admin.tenant_detail.credentials.shelly.fields.server_url')}{' '}
                <span className="text-neutral-400">
                  ({t('platform_admin.tenant_detail.credentials.optional')})
                </span>
              </Label>
              <Input
                id="shelly-serverUrl"
                placeholder="https://shelly-eu.shelly.cloud"
                {...register('serverUrl')}
              />
              <p className="text-[11px] text-neutral-400">
                {t('platform_admin.tenant_detail.credentials.shelly.fields.server_url_hint')}
              </p>
              {fieldError('serverUrl') && (
                <p className="text-xs text-red-600">{fieldError('serverUrl')}</p>
              )}
            </div>

            {serverError && (
              <div
                role="alert"
                className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
              >
                {serverError}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? t('platform_admin.tenant_detail.credentials.validating')
                  : t('platform_admin.tenant_detail.credentials.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
